import styled, { css } from 'styled-components';
import Text from '@components/text';
import Image from '@components/image';
import { TextAlign } from './index';

const getMargin = (align: TextAlign): string => {
  const margins: Record<TextAlign, string> = {
    center: '0 auto',
    left: '0 auto 0 0',
  };
  return margins[align];
};

interface StyledTitleProps {
  quoted?: boolean;
  isLongTitle?: boolean;
}

interface StyledDescriptionProps {
  emphasized?: boolean;
}

export const StyledImage = styled(Image)`
  ${({ theme }) => css`
    width: 100%;
    height: auto;
    object-fit: contain;
    margin-top: 2rem;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      margin-top: 1.75rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      margin-top: 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      margin-top: 1rem;
    }
  `}
`;

export const StyledDescription = styled(Text)<StyledDescriptionProps>`
  ${({ theme, emphasized }) => css`
    ${emphasized &&
    css`
      padding-left: 2.875rem;
      font-weight: 600;
      color: ${theme.colors.secondary};

      font-size: 1.75rem;

      @media only screen and (max-width: ${theme.breakpoints.xxl}) {
        padding-left: 2.875rem;
        font-size: 1.625rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.xl}) {
        padding-left: 2.375rem;
        font-size: 1.5rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        padding-left: 2.375rem;
        font-size: 1.375rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        padding-left: 1.875rem;
        font-size: 1.375rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.sm}) {
        padding-left: 1.25rem;
        font-size: 1.125rem;
      }
    `}
  `}
`;

export const StyledTitle = styled(Text)<StyledTitleProps>`
  ${({ theme, quoted, isLongTitle }) => css`
    ${quoted &&
    css`
      text-indent: -0.5rem;

      &::before {
        content: '"';
      }

      &::after {
        content: '"';
      }

      @media only screen and (max-width: ${theme.breakpoints.sm}) {
        text-indent: -0.9375rem;
        padding-left: 0.9375rem;
      }
    `}
    ${isLongTitle &&
    css`
      font-size: 2.5rem;
      line-height: 1.25;

      @media only screen and (max-width: ${theme.breakpoints.xl}) {
        font-size: 2.125rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        font-size: 1.75rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        font-size: 1.5rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.sm}) {
        font-size: 1.375rem;
        text-indent: ${quoted ? '-0.5rem' : '0'};
      }

      ${quoted &&
      css`
        + ${StyledDescription} {
          padding-left: 1rem;
          text-indent: -0.5rem;
        }
      `}
    `}
  `}
`;

export const Divider = styled.div`
  ${({ theme }) => css`
    border-left: 0.1875rem solid ${theme.colors.primary};
    border-right: 0.1875rem solid ${theme.colors.primary};
    border-radius: 0.1875rem;
    width: 0.375rem;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    transform-origin: top;

    /* Animation */
    transform: scaleY(0);
    animation: divider-grow 0.375s 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards;

    @keyframes divider-grow {
      to {
        transform: scaleY(1);
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      border-left: 0.125rem solid ${theme.colors.primary};
      border-right: 0.125rem solid ${theme.colors.primary};
      width: 0.25rem;
    }
  `}
`;

export const HorizontalDivider = styled.div`
  ${({ theme }) => css`
    border-top: 0.1875rem solid ${theme.colors.primary};
    border-bottom: 0.1875rem solid ${theme.colors.primary};
    border-radius: 0.1875rem;
    height: 0.375rem;
    width: 4.5rem;
    transform-origin: left;
    align-self: center;
    margin-bottom: 0.5rem;

    /* Animation */
    transform: scaleX(0);
    animation: divider-grow 0.375s 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards;

    @keyframes divider-grow {
      to {
        transform: scaleX(1);
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      border-top: 0.125rem solid ${theme.colors.primary};
      border-bottom: 0.125rem solid ${theme.colors.primary};
      height: 0.25rem;
      align-self: flex-start;
      width: 3.5rem;
    }
  `}
`;

interface ContainerProps {
  align?: TextAlign;
  emphasizedDescription?: boolean;
}

export const Container = styled.div<ContainerProps>`
  ${({ theme, align = 'center', emphasizedDescription }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 1.25rem;
    width: 100%;
    max-width: ${emphasizedDescription ? 'unset' : '62rem'};
    margin: ${getMargin(align)};
    text-align: ${align};
    position: relative;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      text-align: left;
    }
  `}
`;
